import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type KeyboardEvent,
} from "react";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";
import { useFieldId } from "./useFieldId";
import { mergeRefs } from "./overlay";
import { cn } from "@/lib/utils";
import { focusRingInsetClass } from "./focus-variants";
import Label from "./Label";
import { FieldHint, fieldHintId } from "./FieldHint";
import {
  fieldControlClass,
  fieldPopoverClass,
  fieldRadius,
  fieldShellClass,
} from "./field-variants";

export interface MultiSelectOption<V extends string = string> {
  readonly value: V;
  readonly label: string;
}

export type MultiSelectProps<V extends string = string> = Omit<
  ComponentPropsWithRef<"div">,
  "children" | "onChange"
> & {
  options: readonly MultiSelectOption<V>[];
  value: V[];
  onChange: (value: V[]) => void;
  placeholderLabel: string;
  /** e.g. `(label) => t("multiSelectRemoveChip", { label })` */
  removeChipLabel: (optionLabel: string) => string;
  label: string;
  hint?: string;
  /** Cap filtered options shown in the listbox. Enables search when set. */
  maxVisibleOptions?: number;
  searchPlaceholderLabel?: string;
  /** Shown when the query can become a new option (filtered count below max). */
  createOptionLabel?: (query: string) => string;
  onCreateOption?: (query: string) => void;
};

function optionLabelFor<V extends string>(
  options: readonly MultiSelectOption<V>[],
  optionValue: V,
): string {
  return options.find((o) => o.value === optionValue)?.label ?? optionValue;
}

function normalizeQuery(query: string): string {
  return query.trim().toLowerCase();
}

export function MultiSelect<V extends string = string>({
  options,
  value,
  onChange,
  placeholderLabel,
  removeChipLabel,
  label,
  hint,
  maxVisibleOptions,
  searchPlaceholderLabel,
  createOptionLabel,
  onCreateOption,
  className,
  id,
  ref,
  ...rest
}: MultiSelectProps<V>) {
  const listboxId = useId();
  const searchInputId = useId();
  const controlId = useFieldId(id);
  const hintId = controlId ? fieldHintId(controlId) : undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const searchable = maxVisibleOptions != null;
  const creatable = onCreateOption != null && createOptionLabel != null;

  const filteredOptions = useMemo(() => {
    const q = normalizeQuery(searchQuery);
    const matched =
      q === ""
        ? options
        : options.filter((option) =>
            option.label.toLowerCase().includes(q),
          );
    if (maxVisibleOptions == null) return matched;
    return matched.slice(0, maxVisibleOptions);
  }, [maxVisibleOptions, options, searchQuery]);

  const trimmedQuery = searchQuery.trim();
  const exactMatchExists = useMemo(() => {
    const q = normalizeQuery(searchQuery);
    if (q === "") return false;
    return options.some((option) => option.label.toLowerCase() === q);
  }, [options, searchQuery]);

  const showCreateOption =
    creatable &&
    trimmedQuery !== "" &&
    !exactMatchExists &&
    (maxVisibleOptions == null || filteredOptions.length < maxVisibleOptions);

  const listItemCount =
    filteredOptions.length + (showCreateOption ? 1 : 0);

  const closeList = useCallback(() => {
    setIsOpen(false);
    setHighlightIndex(-1);
    setSearchQuery("");
    triggerRef.current?.focus();
  }, []);

  const openList = useCallback(() => {
    setIsOpen(true);
    setHighlightIndex(listItemCount > 0 ? 0 : -1);
  }, [listItemCount]);

  const toggleOpen = useCallback(() => {
    if (isOpen) {
      closeList();
    } else {
      openList();
    }
  }, [closeList, isOpen, openList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeList();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeList]);

  useEffect(() => {
    if (!isOpen || !searchable) return;
    searchInputRef.current?.focus();
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) return;
    setHighlightIndex(listItemCount > 0 ? 0 : -1);
  }, [isOpen, listItemCount, searchQuery]);

  const toggleOption = (optionValue: V) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const removeOption = (optionValue: V) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  const commitCreate = () => {
    if (!showCreateOption || onCreateOption == null) return;
    onCreateOption(trimmedQuery);
    setSearchQuery("");
  };

  const selectHighlighted = () => {
    if (highlightIndex < 0) return;
    if (highlightIndex < filteredOptions.length) {
      const option = filteredOptions[highlightIndex];
      if (option) toggleOption(option.value);
      return;
    }
    if (showCreateOption && highlightIndex === filteredOptions.length) {
      commitCreate();
    }
  };

  const moveHighlight = (delta: number) => {
    if (listItemCount === 0) {
      setHighlightIndex(-1);
      return;
    }
    setHighlightIndex((index) => {
      const current = index < 0 ? (delta > 0 ? -1 : 0) : index;
      return (current + delta + listItemCount) % listItemCount;
    });
  };

  const handleListKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeList();
    }
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isOpen) {
      if (
        event.key === "ArrowDown" ||
        event.key === "ArrowUp" ||
        event.key === "Enter" ||
        event.key === " "
      ) {
        event.preventDefault();
        openList();
      }
      return;
    }
    handleListKeyDown(event);
  };

  const handleShellClick = () => {
    toggleOpen();
    triggerRef.current?.focus();
  };

  const shellClass = fieldShellClass({
    className: "cursor-pointer",
  });

  return (
    <div
      className={cn("space-y-1.5", className)}
      ref={mergeRefs(containerRef, ref)}
      {...rest}
    >
      <Label htmlFor={controlId} spacing="field">
        {label}
      </Label>

      <div className="relative">
        <div className={shellClass} onClick={handleShellClick}>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 px-3 py-2">
            {value.length > 0 ? (
              value.map((optionValue) => {
                const chipLabel = optionLabelFor(options, optionValue);
                return (
                  <span
                    key={optionValue}
                    className="inline-flex items-center gap-1 rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
                  >
                    {chipLabel}
                    <button
                      type="button"
                      aria-label={removeChipLabel(chipLabel)}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeOption(optionValue);
                      }}
                      className="rounded hover:bg-primary-200"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </span>
                );
              })
            ) : (
              <span className="text-surface-500">{placeholderLabel}</span>
            )}
          </div>
          <button
            ref={triggerRef}
            type="button"
            id={controlId}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={isOpen ? listboxId : undefined}
            aria-describedby={hint ? hintId : undefined}
            onClick={(event) => {
              event.stopPropagation();
              toggleOpen();
            }}
            onKeyDown={handleTriggerKeyDown}
            className={cn(
              "flex shrink-0 items-center self-stretch px-2 text-surface-400 hover:text-surface-600",
              focusRingInsetClass,
            )}
          >
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isOpen && "rotate-180",
              )}
              aria-hidden
            />
          </button>
        </div>

        {isOpen ? (
          <div
            className={fieldPopoverClass(
              "absolute z-50 mt-1 w-full overflow-hidden",
            )}
          >
            {searchable ? (
              <div className="border-b border-surface-200 p-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                  <input
                    ref={searchInputRef}
                    id={searchInputId}
                    type="text"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onKeyDown={handleListKeyDown}
                    placeholder={searchPlaceholderLabel ?? placeholderLabel}
                    aria-controls={listboxId}
                    className={fieldControlClass({
                      size: "sm",
                      className: cn("pl-9 pr-3", fieldRadius.inner),
                    })}
                  />
                </div>
              </div>
            ) : null}
            <ul
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              className="max-h-60 overflow-auto py-1"
            >
              {filteredOptions.map((option, index) => {
                const isSelected = value.includes(option.value);
                const isHighlighted = index === highlightIndex;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      "flex cursor-pointer items-center justify-between px-3 py-2 text-sm transition-colors",
                      isSelected
                        ? "bg-primary-50 text-primary-700"
                        : "text-surface-700 hover:bg-surface-50",
                      isHighlighted && !isSelected && "bg-surface-100",
                      isHighlighted && isSelected && "bg-primary-100",
                    )}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => toggleOption(option.value)}
                  >
                    <span>{option.label}</span>
                    {isSelected ? (
                      <Check
                        className="h-4 w-4 text-primary-600"
                        aria-hidden
                      />
                    ) : null}
                  </li>
                );
              })}
              {showCreateOption ? (
                <li
                  role="option"
                  aria-selected={false}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-primary-700 transition-colors hover:bg-primary-50",
                    highlightIndex === filteredOptions.length &&
                      "bg-primary-50",
                  )}
                  onMouseEnter={() =>
                    setHighlightIndex(filteredOptions.length)
                  }
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={commitCreate}
                >
                  <Plus className="h-4 w-4 shrink-0" aria-hidden />
                  <span>{createOptionLabel(trimmedQuery)}</span>
                </li>
              ) : null}
              {listItemCount === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-surface-500">
                  {placeholderLabel}
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>

      {hint && hintId ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </div>
  );
}
